import { useCallback, useMemo } from "react";

import {
    calculateHealthRatioFromLtv,
    calculateLiquidationPrice,
    calculateLtvFromLeverage,
    getSimpleInterest,
    LoopWindParams,
    useActiveWallet,
    useLoopWindTransaction,
    LoopExpanded,
    LoopRouteResponseExpanded,
    AbfLoanExpanded,
    getAssetData,
    isWhirlpoolLoop,
    useTokenListMetadataByMints,
    useOraclePrices,
    useUnderlyingTokenInfo,
    isPrincipalTokenWind
} from "@bridgesplit/abf-react";
import {
    bsMath,
    calculatePercentChange,
    decimalsToBps,
    LOADING_ERROR,
    MISSING_PARAM_ERROR,
    MISSING_WALLET_ERROR,
    Result,
    lamportsToUiAmount,
    percentUiToDecimals
} from "@bridgesplit/utils";
import {
    getDurationInSeconds,
    LoanCreationParams,
    getDurationIndex,
    LoopWindType,
    OracleQuote
} from "@bridgesplit/abf-sdk";
import { TrackTransactionEvent } from "app/types";
import { trackSubmitEarnVault } from "app/hooks";

import { DEFAULT_SLIPPAGE } from "../../common";
import { useLoopContext } from "../LoopContext";
import { allTransactionsSucceeded, useTransactionSender } from "../../transactions";
import { useWindContext } from "./WindContext";
import { DEFAULT_WIND_FORM } from "./constants";
import { useExecutionDetails } from "../execution-detail";
import { WindForm } from "./types";

export function useLoopSetWindTokenAmount() {
    const { setForm } = useWindContext();
    return useCallback(
        (amount: number | undefined) => setForm((prev) => ({ ...prev, inputAmount: amount })),
        [setForm]
    );
}

const WIND_ERRORS = {
    "Calculated flash loan amount": "Exceeds desired slippage. You can adjust your slippage in settings",
    "Borrow cap": "Your request exceeds the available borrow capacity for this market"
};
export function useLoopWind() {
    const { loopExpanded } = useLoopContext();
    const { form, setForm, externalQuote, quote, leveragedCollateralAmountToDeposit, LoopWinder, whirlpoolData } =
        useWindContext();
    const loanCreationParams = LoopWinder.getLoanCreationParams({ loopExpanded, form, whirlpoolData });

    return useLoopWindCallback({
        loopExpanded,
        externalQuote,
        bestQuoteLendingStrategyAddress: quote?.bestQuote.lendingStrategyAddress,
        bestQuoteApy: quote?.bestQuote.apy,
        bestQuoteLqt: quote?.bestQuote.liquidationThreshold,
        principalAmountRequested: quote?.principalAmount,
        collateralAmountToDeposit: leveragedCollateralAmountToDeposit,
        multiplier: form.multiplier,
        percentSlippageDecimals: form.percentSlippageDecimals,
        loanCreationParams,
        durationIndex: getDurationIndex(form.strategyDuration),
        depositMint: form.inputToken?.mint,
        loanExpanded: undefined,
        onSuccess: () => setForm(DEFAULT_WIND_FORM) // Form reset on success
    });
}

export function useLoopHealth() {
    const { quote } = useWindContext();
    const { loopExpanded } = useLoopContext();

    return useMemo(() => {
        if (!quote || !loopExpanded) return undefined;
        const { bestQuote, leverageMultiplier } = quote;

        const ltv = calculateLtvFromLeverage(leverageMultiplier);
        const healthRatio = calculateHealthRatioFromLtv(ltv, bestQuote.liquidationThreshold);

        return { ltv, healthRatio, strategyLtv: bestQuote.ltv, strategyLqt: bestQuote.liquidationThreshold };
    }, [loopExpanded, quote]);
}

export function useLoopLiquidationPrice() {
    const { quote } = useWindContext();
    const { loopExpanded } = useLoopContext();
    const { oraclePrices } = useUnderlyingTokenInfo(
        loopExpanded?.principalToken.mint && loopExpanded?.collateralToken.mint
            ? [loopExpanded.principalToken.mint, loopExpanded.collateralToken.mint]
            : []
    );

    return useMemo(() => {
        if (!quote || !loopExpanded)
            return {
                liquidationPrice: 0,
                ltv: undefined,
                currentPrice: 0,
                percentChange: 0
            };

        const { bestQuote, leveragedCollateralAmount, principalAmount } = quote;

        const totalInterest = getSimpleInterest({
            principal: principalAmount,
            apy: bestQuote.apy,
            loanDuration: getDurationInSeconds(bestQuote),
            subtractPrincipal: true
        });

        const liquidationPrice = calculateLiquidationPrice(
            bsMath.tokenAdd(principalAmount, totalInterest, loopExpanded.collateralToken.decimals),
            leveragedCollateralAmount,
            bestQuote.liquidationThreshold
        );

        const currentPrice = bsMath.div(
            oraclePrices?.get(loopExpanded.collateralToken.mint)?.usdPrice,
            oraclePrices?.get(loopExpanded.principalToken.mint)?.usdPrice
        );
        const percentChange = calculatePercentChange(currentPrice, liquidationPrice);

        return { liquidationPrice, ltv: bestQuote.ltv, currentPrice, percentChange };
    }, [loopExpanded, quote, oraclePrices]);
}

export function getLoopWindType(loopExpanded: LoopExpanded) {
    if (loopExpanded.inputToken.mint === loopExpanded.principalToken.mint) {
        return LoopWindType.Principal;
    }
    return LoopWindType.Collateral;
}

export function useLoopWindCallback({
    // fix this to use an optional existing loan and use all correct deltas and params generically
    loopExpanded,
    externalQuote,
    collateralAmountToDeposit,
    multiplier,
    percentSlippageDecimals,
    loanCreationParams,
    onSuccess,
    bestQuoteLendingStrategyAddress,
    bestQuoteApy,
    bestQuoteLqt,
    principalAmountRequested,
    loanExpanded,
    durationIndex,
    depositMint
}: {
    loopExpanded: LoopExpanded | undefined;
    externalQuote: LoopRouteResponseExpanded | undefined; // used for luts
    collateralAmountToDeposit: number | undefined;
    multiplier: number | undefined;
    percentSlippageDecimals: number | undefined;
    loanCreationParams: LoanCreationParams | undefined;
    onSuccess?: () => void;
    bestQuoteLendingStrategyAddress: string | undefined;
    bestQuoteApy: number | undefined;
    bestQuoteLqt: number | undefined;
    principalAmountRequested: number | undefined;
    loanExpanded: AbfLoanExpanded | undefined;
    durationIndex: number | undefined;
    depositMint: string | undefined;
}) {
    const wind = useLoopWindTransaction(); // The original transaction hook
    const send = useTransactionSender();
    const { activeWallet } = useActiveWallet();
    const principalMint = loopExpanded?.principalToken.mint;

    return useCallback(
        async ({
            collateralMint,
            inputMint
        }: {
            collateralMint: string | undefined;
            inputMint: string | undefined;
        }) => {
            if (!loopExpanded) return Result.errFromMessage(LOADING_ERROR);

            if (!activeWallet) return Result.errFromMessage(MISSING_WALLET_ERROR);

            const assetData = getAssetData(loopExpanded.collateralToken, collateralMint);
            if (!externalQuote || !multiplier || !collateralMint || !inputMint || !assetData)
                return Result.errFromMessage(MISSING_PARAM_ERROR);

            const params: LoopWindParams = {
                loopExpanded,
                depositMint: inputMint,
                depositCollateral: [
                    {
                        collateralAmount: collateralAmountToDeposit ?? 0,
                        collateralAssetData: assetData,
                        loanCreationParams: loanCreationParams
                    }
                ],
                leverageMultiplier: multiplier - (percentSlippageDecimals ?? DEFAULT_SLIPPAGE),
                principalRequested: [
                    {
                        ledgerIndex: 0,
                        principalAmount: principalAmountRequested ?? 0,
                        principalMint: principalMint ?? "",
                        strategy: bestQuoteLendingStrategyAddress ?? "",
                        durationIndex: durationIndex ?? 0,
                        expectedLoanValues: {
                            expectedApy: decimalsToBps(bestQuoteApy),
                            expectedLqt: [decimalsToBps(bestQuoteLqt), 0, 0, 0, 0]
                        }
                    }
                ],
                cpiIxs: externalQuote.swapIxs,
                cpiLuts: externalQuote.swapLuts,
                cpiSigners: externalQuote.swapSigners ?? [],
                loanAddress: loanExpanded?.loan.address,
                userPublicKey: activeWallet,
                setupIxs: externalQuote.setupIxs,
                setupSigners: externalQuote.setupSigners ?? [],
                isInitialWind: !loanExpanded // If we have a loan, this is not the initial wind
            };

            const res = await send(wind, params, {
                alerter: { generationEstimateSeconds: 8, showProgress: true },
                messageOverrides: {
                    errorMessageMap: WIND_ERRORS
                },
                isMultiStep: true,
                mixpanelEvent: {
                    key: TrackTransactionEvent.SubmitEarnOrder,
                    params: trackSubmitEarnVault({ params })
                }
            });
            if (allTransactionsSucceeded(res)) {
                onSuccess?.(); // Call optional success callback
            }
            return res;
        },
        [
            loopExpanded,
            activeWallet,
            externalQuote,
            multiplier,
            percentSlippageDecimals,
            loanCreationParams,
            send,
            wind,
            onSuccess,
            bestQuoteLendingStrategyAddress,
            bestQuoteApy,
            bestQuoteLqt,
            principalAmountRequested,
            principalMint,
            durationIndex,
            loanExpanded,
            collateralAmountToDeposit
        ]
    );
}

function useCalculateFairMarketCollateralAmounts() {
    const { loopExpanded } = useLoopContext();
    const {
        oraclePrices,
        externalQuote,
        form: { clmmPosition, inputToken, inputAmount },
        LoopWinder
    } = useWindContext();

    const collateralInputAmount = LoopWinder.getCollateralInputAmount({
        inputAmount,
        clmmPosition,
        inputToken,
        loopExpanded,
        oraclePrices
    });

    return useMemo(() => {
        const principalInputAmount = externalQuote?.inputAmount;
        if (!principalInputAmount || !loopExpanded) {
            return {
                totalFairMarketCollateralAmount: 0,
                swappedFairMarketCollateralOutAmount: 0
            };
        }

        const { principalToken } = loopExpanded;
        const principalOracle = oraclePrices?.get(principalToken.mint);

        // Convert principal amount to USD
        const principalInputAmountUi = lamportsToUiAmount(principalInputAmount, principalToken?.decimals);
        const principalInputAmountUsd = bsMath.mul(principalInputAmountUi, principalOracle?.usdPrice);

        const { collateralToken } = loopExpanded;
        const collateralOracle = oraclePrices?.get(collateralToken.mint);
        // Calculate fair market collateral amount from principal swap
        const swappedFairMarketCollateralOutAmount = bsMath.div(principalInputAmountUsd, collateralOracle?.usdPrice);

        const isFullInputSwapped =
            (isWhirlpoolLoop(loopExpanded) && !clmmPosition) ||
            isPrincipalTokenWind(loopExpanded.principalToken.mint, inputToken?.mint);

        // For full input swapped, only use swapped amount; otherwise add collateral input
        const totalFairMarketCollateralAmount = isFullInputSwapped
            ? swappedFairMarketCollateralOutAmount
            : bsMath.add(swappedFairMarketCollateralOutAmount, collateralInputAmount) ?? 0;

        return {
            totalFairMarketCollateralAmount,
            swappedFairMarketCollateralOutAmount
        };
    }, [externalQuote?.inputAmount, oraclePrices, collateralInputAmount, clmmPosition, loopExpanded, inputToken]);
}

function useCalculateMarketCollateralAmounts() {
    const { externalQuote, quote, LoopWinder } = useWindContext();
    const { loopExpanded } = useLoopContext();

    const orcaMinOutputAmount = useOrcaTotalMinOutputAmount();

    return useMemo(() => {
        return LoopWinder.calculateMarketCollateralAmounts({
            loopExpanded,
            externalQuote,
            quote,
            orcaMinOutputAmount
        });
    }, [orcaMinOutputAmount, LoopWinder, externalQuote, quote, loopExpanded]);
}

interface OrcaMinOutputAmount {
    totalOrcaMinOutputAmount: number;
    swappedOrcaMinOutputAmount: number;
}

function useOrcaTotalMinOutputAmount(): OrcaMinOutputAmount {
    const {
        oraclePrices,
        form: { clmmPosition },
        externalQuote
    } = useWindContext();
    const { loopExpanded } = useLoopContext();

    const inputMint = loopExpanded?.inputToken.mint;
    const inputPrice = oraclePrices?.get(inputMint ?? "")?.usdPrice;
    const mintsOrcaPoolMints = Object.keys(externalQuote?.minOutputTokenAmounts ?? {});

    const { getMetadata } = useTokenListMetadataByMints(mintsOrcaPoolMints);
    const { getUsdPrice } = useOraclePrices(mintsOrcaPoolMints);

    const orcaMinOutputAmount = useMemo(() => {
        // if there is a selected orca position include that in the total position price because jup response does not include that in min outs
        const currentPositionInputAmount = bsMath.div(clmmPosition?.totalPrice, inputPrice) ?? 0;
        let inputAmountSwapped = 0;
        for (const [mint, amount] of Object.entries(externalQuote?.minOutputTokenAmounts ?? {})) {
            const decimals = getMetadata(mint)?.decimals;
            const amountUi = lamportsToUiAmount(amount, decimals);

            if (mint === inputMint) {
                inputAmountSwapped += amountUi;
            } else {
                const usdPrice = getUsdPrice(mint);
                const usdValue = bsMath.mul(amountUi, usdPrice);
                inputAmountSwapped += bsMath.div(usdValue, inputPrice);
            }
        }

        return {
            totalOrcaMinOutputAmount: inputAmountSwapped,
            swappedOrcaMinOutputAmount: inputAmountSwapped - (currentPositionInputAmount ?? 0) // when swapping with principal the outswapped amount includes the original input amount
        };
    }, [
        externalQuote?.minOutputTokenAmounts,
        inputMint,
        inputPrice,
        getMetadata,
        getUsdPrice,
        clmmPosition?.totalPrice
    ]);

    return orcaMinOutputAmount;
}

export function useCalculateFairAndMarketValues() {
    const { loopExpanded } = useLoopContext();
    const { oraclePrices } = useWindContext();

    const { totalFairMarketCollateralAmount, swappedFairMarketCollateralOutAmount } =
        useCalculateFairMarketCollateralAmounts();

    const { totalMinOutputCollateralAmount, swappedMinOutputCollateralAmount } = useCalculateMarketCollateralAmounts();

    return useMemo(() => {
        const collateralPrice = oraclePrices?.get(loopExpanded?.collateralToken.mint ?? "")?.usdPrice;
        const swappedFairMarketValueUsd = bsMath.mul(swappedFairMarketCollateralOutAmount, collateralPrice);

        const totalFairMarketValueUsd = bsMath.mul(totalFairMarketCollateralAmount, collateralPrice);

        const totalReceivedMarketValueUsd = bsMath.mul(totalMinOutputCollateralAmount, collateralPrice);

        const swappedReceivedMarketValueUsd = bsMath.mul(swappedMinOutputCollateralAmount, collateralPrice);

        return {
            totalFairMarketValueUsd,
            totalReceivedMarketValueUsd,
            swappedFairMarketValueUsd,
            swappedReceivedMarketValueUsd
        };
    }, [
        totalFairMarketCollateralAmount,
        swappedFairMarketCollateralOutAmount,
        totalMinOutputCollateralAmount,
        swappedMinOutputCollateralAmount,
        oraclePrices,
        loopExpanded?.collateralToken.mint
    ]);
}

/**
 * Floors to 0.1 precision and clamps to max leverage. Used in WindMultiplier and ManageLeverage for debounced multipliers.
 */
export function floorAndClamp(raw: number, maxLeverage: number): number {
    const floored = Math.floor(raw * 10) / 10;
    return Math.min(floored, Math.floor(maxLeverage * 10) / 10);
}

export function useWindExecutionDetails({
    externalQuote,
    form,
    principalOracle,
    collateralOracle
}: {
    externalQuote: LoopRouteResponseExpanded | undefined;
    form: WindForm;
    principalOracle: OracleQuote | undefined;
    collateralOracle: OracleQuote | undefined;
}) {
    const {
        totalFairMarketValueUsd,
        totalReceivedMarketValueUsd,
        swappedFairMarketValueUsd,
        swappedReceivedMarketValueUsd
    } = useCalculateFairAndMarketValues();

    // we want the stats to only back out the slippage and price impact for the swapped amount
    // if we included the entire collateral amount here, it would magnify those stats
    const executionDetails = useExecutionDetails({
        principalOracle,
        slippagePercentDecimals: form.percentSlippageDecimals ?? percentUiToDecimals(DEFAULT_SLIPPAGE),
        externalQuote: externalQuote,
        receiveUsdAmount: swappedReceivedMarketValueUsd,
        leftOverPrincipal: 0,
        fairMarketValue: swappedFairMarketValueUsd
    });

    // we want the ui to show the total collateral of the position
    const finalExecutionDetails =
        executionDetails && totalFairMarketValueUsd && totalReceivedMarketValueUsd
            ? {
                  ...executionDetails,
                  fairMarketValue: totalFairMarketValueUsd,
                  receiveUsdAmount: totalReceivedMarketValueUsd
              }
            : executionDetails;

    if (!externalQuote)
        return { executionDetails: undefined, receivedMarketValueUsd: undefined, receivedTokenValue: undefined };

    const receivedTokenValue = bsMath.div(totalReceivedMarketValueUsd, collateralOracle?.usdPrice);

    return { executionDetails: finalExecutionDetails, receivedTokenValue };
}
